import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import apiService from "../services/apiService";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import EmailConfigModal from "../Components/EmailConfigModal";

/* EXACT COLORS FROM SECOND IMAGE */
const BLUE_PRIMARY = "#1f5582";
const BLUE_CARD = "#2e75b6";
const BLUE_LIGHT = "#e8f4fd";
const WHITE_BG = "#ffffff";

const ExactDailyStatusDashboard = forwardRef(({ 
  selectedApplication = "",
  applicationName = "",
  selectedEnvironment = "",
  dateMonitored: propDateMonitored,
  setDateMonitored: propSetDateMonitored,
  showEmailSubject: propShowEmailSubject,
  setShowEmailSubject: propSetShowEmailSubject,
  emailConfigOpen: propEmailConfigOpen,
  setEmailConfigOpen: propSetEmailConfigOpen,
}, ref) => {
  const dashboardRef = useRef(null);
  const highlightRef = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ open: false, message: '', severity: 'info' });
  const [customEmailSubject, setCustomEmailSubject] = useState("");

  // Use props if provided, otherwise use internal state
  const [internalDateMonitored, setInternalDateMonitored] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [internalShowEmailSubject, setInternalShowEmailSubject] = useState(false);
  const [internalEmailConfigOpen, setInternalEmailConfigOpen] = useState(false);

  const dateMonitored = propDateMonitored !== undefined ? propDateMonitored : internalDateMonitored;
  const setDateMonitored = propSetDateMonitored || setInternalDateMonitored;
  const showEmailSubject = propShowEmailSubject !== undefined ? propShowEmailSubject : internalShowEmailSubject;
  const setShowEmailSubject = propSetShowEmailSubject || setInternalShowEmailSubject;
  const emailConfigOpen = propEmailConfigOpen !== undefined ? propEmailConfigOpen : internalEmailConfigOpen;
  const setEmailConfigOpen = propSetEmailConfigOpen || setInternalEmailConfigOpen;

  /* 🔹 DATE FILTER */

  // Helper function to convert date string to Date object
  const toDateObj = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
  };

  /* 🔹 KPI STATE */
  const [kpiData, setKpiData] = useState({
    jobsMonitored: 0,
    garpacJobs: 0,
    omegaJobs: 0,
    jobsFailed: 0,
    caPickInvoiced: 0,
    agingOrders: 0,
  });

  /* 🔹 GRAPH DATA STATE */
  const [belJobs, setBelJobs] = useState([
    { label: "EUR", count: 0 },
    { label: "GBR", count: 0 },
  ]);

  const [usmJobs, setUsmJobs] = useState([]);

  /* 🔹 PKMS TICKETS COUNT STATE */
  const [pkmsTicketsCount, setPkmsTicketsCount] = useState(0);

  /* 🔹 SNOW TICKETS STATE */
  const [snowTickets, setSnowTickets] = useState([
    { ticket: "CHG Rtl", count: 0 },
    { ticket: "WOF", count: 0 },
    { ticket: "LDAP", count: 0 },
  ]);

  /* 🔹 GENERATE DYNAMIC TITLE */
  const generateTitle = () => {
    if (!dateMonitored) {
      return "GARPAC/Omega Daily Status Report (07.00 PM - 08.00 AM)";
    }

    // Parse the selected date (dateMonitored is already in YYYY-MM-DD format)
    const selectedDate = new Date(dateMonitored + 'T00:00:00'); // Add time to avoid timezone issues
    
    // Format date as MM-DD-YYYY
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const year = selectedDate.getFullYear();
    const formattedDate = `${month}-${day}-${year}`;
    
    // Create time range (07.00 PM - 08.00 AM next day)
    const timeRange = "(07.00 PM - 08.00 AM)";
    
    // Determine application name for title
    let appName;
    if (!selectedApplication || selectedApplication === 'ALL') {
      // No application selected or ALL selected - show GARPAC/Omega
      appName = 'GARPAC/Omega';
    } else if (selectedApplication === 'GARPAC' || selectedApplication === 'Omega') {
      // GARPAC or Omega selected - show GARPAC/Omega
      appName = 'GARPAC/Omega';
    } else {
      // Other specific application selected - show that application name
      appName = applicationName || selectedApplication;
    }
    
    return `${appName} Daily Status Report ${formattedDate} ${timeRange}`;
  };

  // Update custom email subject when application or date changes
  useEffect(() => {
    const generateDefaultSubject = () => {
      if (!dateMonitored) {
        return "Daily Report - GARPAC/Omega";
      }

      const selectedDate = new Date(dateMonitored + 'T00:00:00');
      const formattedDate = selectedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      // Determine application name for email subject
      let appName;
      if (!selectedApplication || selectedApplication === 'ALL') {
        appName = 'GARPAC/Omega';
      } else if (selectedApplication === 'GARPAC' || selectedApplication === 'Omega') {
        appName = 'GARPAC/Omega';
      } else {
        appName = applicationName || selectedApplication;
      }
      
      return `Daily Report - ${appName} Job Monitoring - ${formattedDate}`;
    };

    const defaultSubject = generateDefaultSubject();
    setCustomEmailSubject(defaultSubject);
  }, [selectedApplication, applicationName, dateMonitored]);

  const [highlightsText, setHighlightsText] = useState("");

  const highlightLines = highlightsText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  /* 🔹 FETCH REPORT HIGHLIGHTS */
  useEffect(() => {
    const fetchReportHighlights = async () => {
      try {
        if (!dateMonitored) return;

        const params = new URLSearchParams({ date: dateMonitored });
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        const data = await apiService.get(`/garpac-report/get-highlights?${params}`);
        setHighlightsText(data.highlights || "");
      } catch (error) {
        console.error("Error fetching report highlights:", error);
        setHighlightsText("");
      }
    };

    fetchReportHighlights();
  }, [dateMonitored, selectedApplication, selectedEnvironment]);

  /* 🔹 SAVE REPORT HIGHLIGHTS */
  const saveHighlights = async () => {
    try {
      if (!dateMonitored) return;

      // Use the generated title automatically
      const autoTitle = generateTitle();

      const payload = {
        title: autoTitle,
        highlights: highlightsText,
        date: dateMonitored,
      };

      if (selectedApplication && selectedApplication !== 'ALL') {
        payload.application = selectedApplication;
      }
      if (selectedEnvironment && selectedEnvironment !== 'ALL') {
        payload.env_id = selectedEnvironment;
      }

      await apiService.post("/garpac-report/save-highlights", payload);

      console.log("Highlights saved successfully");
      setEditMode(false);
    } catch (error) {
      console.error("Error saving highlights:", error);
    }
  };

  /* CLICK OUTSIDE */
  useEffect(() => {
    const handleClickOutside = async (e) => {
      if (
        editMode &&
        highlightRef.current &&
        !highlightRef.current.contains(e.target)
      ) {
        // Save when clicking outside
        try {
          if (dateMonitored) {
            // Generate title inline to avoid dependency issues
            const selectedDate = new Date(dateMonitored + 'T00:00:00');
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const year = selectedDate.getFullYear();
            const formattedDate = `${month}-${day}-${year}`;
            
            // Determine application name for title
            let appName;
            if (!selectedApplication || selectedApplication === 'ALL') {
              appName = 'GARPAC/Omega';
            } else if (selectedApplication === 'GARPAC' || selectedApplication === 'Omega') {
              appName = 'GARPAC/Omega';
            } else {
              appName = applicationName || selectedApplication;
            }
            
            const autoTitle = `${appName} Daily Status Report ${formattedDate} (07.00 PM - 08.00 AM)`;
            
            const payload = {
              title: autoTitle,
              highlights: highlightsText,
              date: dateMonitored,
            };

            if (selectedApplication && selectedApplication !== 'ALL') {
              payload.application = selectedApplication;
            }
            if (selectedEnvironment && selectedEnvironment !== 'ALL') {
              payload.env_id = selectedEnvironment;
            }
            
            await apiService.post("/garpac-report/save-highlights", payload);
            console.log("Highlights saved successfully");
          }
        } catch (error) {
          console.error("Error saving highlights:", error);
        }
        setEditMode(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editMode, highlightsText, dateMonitored, selectedApplication, applicationName, selectedEnvironment]);

  /* 🔹 FETCH ALL KPIs */
  useEffect(() => {
    const fetchAllKPIs = async () => {
      try {
        const params = new URLSearchParams();
        if (dateMonitored) {
          params.append('date_monitored', dateMonitored);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        let endpoint = "/garpac-report/all-kpis";
        if (params.toString()) {
          endpoint += `?${params}`;
        }

        const data = await apiService.get(endpoint);
        setKpiData({
          jobsMonitored: data.jobsMonitored || 0,
          garpacJobs: data.garpacJobs || 0,
          omegaJobs: data.omegaJobs || 0,
          jobsFailed: data.jobsFailed || 0,
          caPickInvoiced: data.caPickCount || 0,
          agingOrders: data.agingOrdersCount || 0,
        });
      } catch (error) {
        console.error("Error fetching KPIs:", error);
        setKpiData({
          jobsMonitored: 0,
          garpacJobs: 0,
          omegaJobs: 0,
          jobsFailed: 0,
          caPickInvoiced: 0,
          agingOrders: 0,
        });
      }
    };

    fetchAllKPIs();
  }, [dateMonitored, selectedApplication, selectedEnvironment]);

  /* 🔹 FETCH BELGPWHSAPP03 JOBS DATA */
  useEffect(() => {
    const fetchBelgpwhsapp03Jobs = async () => {
      try {
        const params = new URLSearchParams();
        if (dateMonitored) {
          params.append('date_monitored', dateMonitored);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        let endpoint = "/garpac-report/belgpwhsapp03-jobs";
        if (params.toString()) {
          endpoint += `?${params}`;
        }

        const data = await apiService.get(endpoint);
        setBelJobs(data || [
          { label: "EUR", count: 0 },
          { label: "GBR", count: 0 },
        ]);
      } catch (error) {
        console.error("Error fetching belgpwhsapp03 jobs:", error);
        setBelJobs([
          { label: "EUR", count: 0 },
          { label: "GBR", count: 0 },
        ]);
      }
    };

    fetchBelgpwhsapp03Jobs();
  }, [dateMonitored, selectedApplication, selectedEnvironment]);

  /* 🔹 FETCH USMBPWHSAPP03 JOBS DATA */
  useEffect(() => {
    const fetchUsmbpwhsapp03Jobs = async () => {
      try {
        const params = new URLSearchParams();
        if (dateMonitored) {
          params.append('date_monitored', dateMonitored);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        let endpoint = "/garpac-report/usmbpwhsapp03-jobs";
        if (params.toString()) {
          endpoint += `?${params}`;
        }

        const data = await apiService.get(endpoint);
        setUsmJobs(data || []);
      } catch (error) {
        console.error("Error fetching usmbpwhsapp03 jobs:", error);
        setUsmJobs([]);
      }
    };

    fetchUsmbpwhsapp03Jobs();
  }, [dateMonitored, selectedApplication, selectedEnvironment]);

  /* 🔹 FETCH PKMS TICKETS COUNT */
  useEffect(() => {
    const fetchPkmsTicketsCount = async () => {
      try {
        const params = new URLSearchParams();
        if (dateMonitored) {
          params.append('date_monitored', dateMonitored);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        let endpoint = "/garpac-report/pkms-tickets-count";
        if (params.toString()) {
          endpoint += `?${params}`;
        }

        const data = await apiService.get(endpoint);
        setPkmsTicketsCount(data.pkmsTicketsCount || 0);
      } catch (error) {
        console.error("Error fetching PKMS tickets count:", error);
        setPkmsTicketsCount(0);
      }
    };

    fetchPkmsTicketsCount();
  }, [dateMonitored, selectedApplication, selectedEnvironment]);

  /* 🔹 FETCH SNOW TICKETS DATA */
  useEffect(() => {
    const fetchSnowTicketsData = async () => {
      try {
        const params = new URLSearchParams();
        if (dateMonitored) {
          params.append('date_monitored', dateMonitored);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        let endpoint = "/garpac-report/snow-tickets";
        if (params.toString()) {
          endpoint += `?${params}`;
        }

        const data = await apiService.get(endpoint);
        
        // Update the snow tickets with real data
        const updatedTickets = [
          { ticket: "CHG Rtl", count: data.changeRtlCount || 0 },
          { ticket: "WOF", count: data.wofCount || 0 },
          { ticket: "LDAP", count: data.ldapCount || 0 },
        ];
        
        setSnowTickets(updatedTickets);
      } catch (error) {
        console.error("Error fetching SNOW tickets:", error);
        setSnowTickets([
          { ticket: "CHG Rtl", count: 0 },
          { ticket: "WOF", count: 0 },
          { ticket: "LDAP", count: 0 },
        ]);
      }
    };

    fetchSnowTicketsData();
  }, [dateMonitored, selectedApplication, selectedEnvironment]);

  const downloadDashboard = async () => {
    if (!dashboardRef.current) return;
    const canvas = await html2canvas(dashboardRef.current, {
      scale: 2,
      useCORS: true,
    });
    const link = document.createElement("a");
    link.download = "daily_status_report.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const sendReportEmail = async () => {
    if (!dashboardRef.current) return;
    
    setSendingEmail(true);
    setEmailStatus({ open: false, message: '', severity: 'info' });

    try {
      // Prepare report data
      const reportData = {
        title: generateTitle(),
        date: dateMonitored,
        application: selectedApplication,
        highlights: highlightsText,
        kpis: kpiData
      };

      try {
        // First, try to send with image
        const canvas = await html2canvas(dashboardRef.current, {
          scale: 1.5, // Reduced from 2 to 1.5 for smaller file size
          useCORS: true,
          quality: 0.8, // Add quality setting
          backgroundColor: '#ffffff'
        });
        
        // Convert to JPEG for smaller file size, then to base64
        const imageData = canvas.toDataURL("image/jpeg", 0.8); // JPEG with 80% quality

        // Check if image is too large (rough estimate)
        const imageSizeKB = Math.round(imageData.length / 1024);
        console.log(`Image size: ${imageSizeKB}KB`);

        if (imageSizeKB > 5000) { // If larger than 5MB, use text-only
          throw new Error('Image too large, using text-only email');
        }

        // Send email with image
        const response = await apiService.post('/email/send-report', {
          reportType: 'daily',
          reportData,
          imageData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || 'Daily report sent successfully with image!',
          severity: 'success'
        });

      } catch (imageError) {
        console.log('Image email failed, trying text-only:', imageError.message);
        
        // Fallback to text-only email
        const response = await apiService.post('/email/send-report-text', {
          reportType: 'daily',
          reportData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || 'Daily report sent successfully (text format)!',
          severity: 'success'
        });
      }

    } catch (error) {
      console.error('Error sending email:', error);
      
      let errorMessage = 'Failed to send email. Please check your email configuration.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        
        // If no email configuration found, suggest opening settings
        if (errorMessage.includes('No email configuration found')) {
          errorMessage += ' Click the settings icon (⚙️) to configure email recipients.';
        } else if (errorMessage.includes('Email configuration is disabled')) {
          errorMessage = 'Email is configured but disabled. Click the settings icon (⚙️) to enable email sending.';
        }
      }
      
      setEmailStatus({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    downloadDashboard,
    sendEmail: sendReportEmail,
  }));

  return (
   <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#EBF3FF",
        p: { xs: 1, md: 2 },
      }}
    >
      {/* Email Subject Input - Controlled from container */}
      {showEmailSubject && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Email Subject"
            value={customEmailSubject}
            onChange={(e) => setCustomEmailSubject(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
                '&:hover fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
                '&.Mui-focused fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
              },
              '& .MuiInputLabel-root': {
                color: BLUE_PRIMARY,
                '&.Mui-focused': {
                  color: BLUE_PRIMARY,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Main Dashboard Card */}
      <Box
        ref={dashboardRef}
        sx={{
          border: `2px solid ${BLUE_PRIMARY}`,
          borderRadius: 2,
          bgcolor: WHITE_BG,
          p: { xs: 1.5, md: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Typography
          variant="h6"
          align="center"
          fontWeight="bold"
          color={BLUE_PRIMARY}
        >
          {generateTitle()}
        </Typography>

        {/* KPI Tiles */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 1.5,
          }}
        >
          {[
            { 
              label: "Jobs Monitored", 
              value: kpiData.jobsMonitored 
            },
            { 
              label: `${applicationName || 'GARPAC'} Jobs`, 
              value: kpiData.garpacJobs 
            },
            { 
              label: applicationName === "GARPAC" ? "Omega Jobs" : applicationName === "Omega" ? "Other Jobs" : "Other Jobs", 
              value: kpiData.omegaJobs 
            },
            { 
              label: "Jobs Failed", 
              value: kpiData.jobsFailed 
            },
            { 
              label: "CA Missing Picks", 
              value: kpiData.caPickInvoiced 
            },
            { 
              label: "Aging Orders", 
              value: kpiData.agingOrders 
            },
          ].map((item, idx) => (
            <Paper
              key={idx}
              elevation={3}
              sx={{
                bgcolor: BLUE_CARD,
                color: "white",
                p: 1.5,
                textAlign: "center",
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9, display: "block" }}>
                {item.label}
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {item.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Charts + PKMS/SNOW */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 2,
            height: { xs: "auto", lg: 260 },
          }}
        >
          {/* BEL Chart */}
          <Card sx={{ flex: "0 0 240px", display: "flex", flexDirection: "column" }}>
            <Box bgcolor={BLUE_CARD} color="white" p={1} textAlign="center">
              <Typography variant="caption" fontWeight={600}>
                {applicationName ? `${applicationName} Jobs in belgpwhsapp03` : "Jobs Monitored in belgpwhsapp03"}
              </Typography>
            </Box>
            <CardContent sx={{ flex: 1, p: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={belJobs}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={BLUE_CARD}>
                    <LabelList dataKey="count" position="top" style={{ fontSize: 12 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* USM Chart */}
          <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box bgcolor={BLUE_CARD} color="white" p={1} textAlign="center">
              <Typography variant="caption" fontWeight={600}>
                {applicationName ? `${applicationName} Jobs in usmbpwhsapp03` : "Jobs Monitored in usmbpwhsapp03"}
              </Typography>
            </Box>
            <CardContent sx={{ flex: 1, p: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={usmJobs}>
                  <XAxis
                    dataKey="label"
                    
                    textAnchor="end"
                    
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={BLUE_CARD}>
                    <LabelList dataKey="count" position="top" style={{ fontSize: 12 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* PKMS + SNOW */}
          <Box
            sx={{
              width: { xs: "100%", sm: 180, lg: 160 },
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Card sx={{ flex: "0 0 90px", display: "flex", flexDirection: "column" }}>
              <Box bgcolor={BLUE_CARD} color="white" p={0.8} textAlign="center">
                <Typography variant="caption" fontWeight={600} fontSize="0.8rem">
                  # Pick ticket to PKMS
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    bgcolor: BLUE_LIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" color={BLUE_CARD}>
                    {pkmsTicketsCount}
                  </Typography>
                </Box>
              </Box>
            </Card>

            <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Box bgcolor={BLUE_CARD} color="white" p={0.8} textAlign="center">
                <Typography variant="caption" fontWeight={600} fontSize="0.8rem">
                  SNOW Tickets
                </Typography>
              </Box>
              <CardContent sx={{ flex: 1, p: 0.8 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: "0.75rem", py: 0.5, px: 1, bgcolor: BLUE_LIGHT }}>
                        Tickets
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          py: 0.5,
                          px: 1,
                          textAlign: "center",
                          bgcolor: BLUE_LIGHT,
                        }}
                      >
                        Count
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {snowTickets.map((row) => (
                      <TableRow key={row.ticket}>
                        <TableCell sx={{ fontSize: "0.8rem", py: 0.5, px: 1 }}>
                          {row.ticket}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", py: 0.5, px: 1, textAlign: "center" }}>
                          {row.count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Highlights */}
        <Box
          ref={highlightRef}
          onClick={() => !editMode && setEditMode(true)}
          sx={{
            border: `2px solid ${BLUE_PRIMARY}`,
            borderRadius: 1,
            p: 2,
            bgcolor: WHITE_BG,
            flex: 1,
            minHeight: 140,
            cursor: editMode ? "default" : "pointer",
          }}
        >
          <Typography variant="subtitle1" color={BLUE_PRIMARY} fontWeight="600" gutterBottom>
            Highlights:
          </Typography>

          {!editMode ? (
            <Box sx={{ pl: 1 }}>
              {highlightLines.length === 0 ? (
                <Typography color="text.secondary" fontSize="0.95rem">
                  Click to add/edit highlights...
                </Typography>
              ) : (
                highlightLines.map((line, i) => (
                  <Typography key={i} sx={{ mb: 0.6, fontSize: "0.94rem" }}>
                    • {line}
                  </Typography>
                ))
              )}
            </Box>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={5}
                maxRows={8}
                value={highlightsText}
                onChange={(e) => setHighlightsText(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2, "& .MuiInputBase-input": { fontSize: "0.93rem" } }}
              />
              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                <Button size="small" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveHighlights}
                  sx={{ bgcolor: BLUE_PRIMARY }}
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Email Configuration Modal */}
      <EmailConfigModal 
        reportType="daily"
        open={emailConfigOpen} 
        onClose={() => setEmailConfigOpen(false)} 
      />

      {/* Email Status Snackbar */}
      <Snackbar
        open={emailStatus.open}
        autoHideDuration={6000}
        onClose={() => setEmailStatus({ ...emailStatus, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setEmailStatus({ ...emailStatus, open: false })} 
          severity={emailStatus.severity}
          sx={{ width: '100%' }}
        >
          {emailStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ExactDailyStatusDashboard;

